/**
 * CreditImprovement — Phase F2.2 dedicated Credit Improvement page.
 *
 * Route: `/credit-improvement`.
 *
 * Responsibility split with `/credit-report` (Credit Readiness):
 *   - `/credit-report` (Readiness): score + tier + strengths/weaknesses/
 *     blockers/timeline + section-level analysis.
 *   - `/credit-improvement` (this page): findings-first view — every
 *     negative item with select/edit-reason/custom-note controls,
 *     priority buckets, filters, and a dispute-preparation entry point
 *     (the CTA will be hooked to the Phase F3 dispute-letter flow).
 *
 * Empty state: renders the F1 IdentityIQ connect card (no upload UI
 * reintroduced). Feature-flag `credit_engine_v2` gates the page — when
 * OFF, we render a neutral message pointing the user at the IdentityIQ
 * connection.
 *
 * V2 backend endpoints consumed:
 *   - `GET /api/credit-reports/latest`      → PublicCreditReport with
 *                                             `negative_items[]` (V2
 *                                             fields when the flag is on).
 *   - `PATCH /api/credit-reports/{id}/negative-items/{item_id}`
 *                                          → select / edit reason / note
 *                                             (handled inside the shared
 *                                             NegativeItemCard).
 *
 * Voice guardrails: no banned language. The required disclaimer is
 * rendered inline above the findings list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  Info,
  Filter as FilterIcon,
  ClipboardList,
  ArrowRight,
} from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import FeatureDisabled from "@/feature-flags/FeatureDisabled";
import AppNav from "@/components/app/AppNav";
import NegativeItemCard from "@/components/credit-report/NegativeItemCard";
import IdentityIQConnectCard from "@/components/credit/IdentityIQConnectCard";

const CATEGORY_LABELS = {
  collection: "Collection",
  charge_off: "Charge-off",
  medical_collection: "Medical collection",
  paid_collection: "Paid collection",
  late_30: "30-day late",
  late_60: "60-day late",
  late_90: "90-day late",
  late_120: "120-day late",
  bankruptcy: "Bankruptcy",
  foreclosure: "Foreclosure",
  repossession: "Repossession",
  settled_less_than_full: "Settled for less",
  student_loan_late: "Student-loan late",
  derogatory_remark: "Derog remark",
  duplicate_account: "Duplicate",
  incorrect_balance: "Incorrect balance",
  incorrect_date: "Incorrect date",
  unknown_account: "Unknown account",
  post_transfer_reporting: "Post-transfer reporting",
  inconsistent_bureau_reporting: "Inconsistent bureau",
  public_record: "Public record",
  other: "Other",
};

const PRIORITY_ORDER = ["high", "medium", "low"];
const PRIORITY_META = {
  high:   { Icon: AlertOctagon,   label: "High priority",   color: "text-[var(--ds-color-danger)]",  ring: "bg-[var(--ds-color-danger)]" },
  medium: { Icon: AlertTriangle,  label: "Medium priority", color: "text-[var(--ds-color-warning)]", ring: "bg-[var(--ds-color-warning)]" },
  low:    { Icon: Info,           label: "Low priority",    color: "text-[var(--ds-color-info)]",    ring: "bg-[var(--ds-color-info)]" },
};

export default function CreditImprovement() {
  const { isEnabled } = useFeatureFlags();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [siteSettings, setSiteSettings] = useState(null);
  const [report, setReport] = useState(null);

  // Local UI state.
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [bureauFilter, setBureauFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    async function boot() {
      setLoading(true);
      setError("");
      try {
        const [reportRes, settingsRes] = await Promise.all([
          api.get("/credit-reports/latest").catch((e) => {
            if (e?.response?.status === 404) return { data: null };
            throw e;
          }),
          api.get("/site-settings/public").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setReport(reportRes?.data ?? null);
        setSiteSettings(settingsRes?.data ?? null);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    }
    boot();
    return () => { alive = false; };
  }, []);

  const allItems = useMemo(() => report?.negative_items ?? [], [report]);

  const bureausAvailable = useMemo(() => {
    const seen = new Set();
    for (const it of allItems) {
      String(it.bureau_reporting || "")
        .split(/[,/;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((b) => seen.add(b));
    }
    return Array.from(seen);
  }, [allItems]);

  const categoriesAvailable = useMemo(() => {
    const seen = new Set();
    for (const it of allItems) if (it.category) seen.add(it.category);
    return Array.from(seen);
  }, [allItems]);

  const filteredItems = useMemo(() => {
    return allItems.filter((it) => {
      if (priorityFilter !== "all" && (it.priority || "low").toLowerCase() !== priorityFilter) return false;
      if (bureauFilter !== "all") {
        const bureaus = String(it.bureau_reporting || "")
          .split(/[,/;]+/)
          .map((s) => s.trim());
        if (!bureaus.includes(bureauFilter)) return false;
      }
      if (categoryFilter !== "all" && it.category !== categoryFilter) return false;
      return true;
    });
  }, [allItems, priorityFilter, bureauFilter, categoryFilter]);

  const priorityCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    for (const it of allItems) counts[(it.priority || "low").toLowerCase()] += 1;
    return counts;
  }, [allItems]);

  const selectedCount = useMemo(
    () => allItems.filter((it) => it.user_selected).length,
    [allItems],
  );

  // Phase F3 — the wizard reads `credit_report_id` and `items` from the
  // URL query string (see DisputeLetterWizard.jsx). We materialise the
  // selected IDs at the CTA layer so a normal click carries them across;
  // passing them via React Router `state` was silently dropped at the
  // wizard boundary and forced users to type the URL by hand.
  const selectedItemIds = useMemo(
    () => allItems.filter((it) => it.user_selected).map((it) => it.id),
    [allItems],
  );

  function handleItemPatched(nextItems) {
    setReport((prev) => (prev ? { ...prev, negative_items: nextItems } : prev));
  }

  // -----------------------------------------------------------------------
  // Feature-flag gate — the whole page is inert if credit_engine_v2 is OFF.
  // -----------------------------------------------------------------------
  if (!isEnabled("credit_engine_v2")) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <FeatureDisabled
          testId="credit-improvement-feature-disabled"
          title="Credit analysis is temporarily disabled"
          message="An administrator has paused Credit Improvement. Your existing V2 findings remain in the system and will re-appear when the feature is re-enabled."
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <div
          data-testid="credit-improvement-loading"
          className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-card)] p-8 text-center text-[var(--ds-color-ink-muted)]"
        >
          Loading your Credit Improvement findings…
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <div
          data-testid="credit-improvement-error"
          className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-danger-soft)] border border-[var(--ds-color-danger)]/30 shadow-[var(--ds-shadow-card)] p-6 text-[var(--ds-color-danger)]"
        >
          {error}
        </div>
      </Shell>
    );
  }

  // -----------------------------------------------------------------------
  // Empty state — reuses the F1 IdentityIQ connect card. No upload UI.
  // -----------------------------------------------------------------------
  if (!report || !allItems.length) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <section className="space-y-6">
          <PageHeader
            eyebrow="Credit Improvement"
            title="Nothing to review yet"
            subtitle="Connect your IdentityIQ account and we&rsquo;ll surface every negative item across all three bureaus with a suggested dispute path."
          />
          <IdentityIQConnectCard
            variant="page"
            testIdPrefix="credit-improvement-empty"
            disclaimer={siteSettings?.disclaimer_text}
            consent={siteSettings?.upload_consent_text}
          />
        </section>
      </Shell>
    );
  }

  // -----------------------------------------------------------------------
  // Populated state — full findings list.
  // -----------------------------------------------------------------------
  return (
    <Shell disclaimer={siteSettings?.disclaimer_text}>
      <section data-testid="credit-improvement-root" className="space-y-6">
        <PageHeader
          eyebrow="Credit Improvement"
          title="Every finding, ready to dispute"
          subtitle="We surface every negative item detected across your three bureaus, group them by priority, and pre-draft a dispute reason grounded in real FCRA language. Review each one, edit any reason, and select the items you want to include when you generate your dispute letters."
        />

        <SummaryHeader
          totalCount={allItems.length}
          priorityCounts={priorityCounts}
          selectedCount={selectedCount}
          selectedItemIds={selectedItemIds}
          reportId={report.id}
        />

        <InlineDisclaimer />

        <FiltersRow
          priorityFilter={priorityFilter}
          onPriorityChange={setPriorityFilter}
          bureauFilter={bureauFilter}
          onBureauChange={setBureauFilter}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          bureausAvailable={bureausAvailable}
          categoriesAvailable={categoriesAvailable}
        />

        <FindingsList
          items={filteredItems}
          reportId={report.id}
          onItemPatched={handleItemPatched}
        />

        {filteredItems.length === 0 ? (
          <div
            data-testid="credit-improvement-filter-empty"
            className="rounded-[var(--ds-radius-lg)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface-muted)] p-6 text-center text-[var(--ds-color-ink-muted)]"
          >
            No findings match the current filters. Clear a filter to see more.
          </div>
        ) : null}
      </section>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Shell({ children, disclaimer }) {
  return (
    <div className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]">
      <AppNav />
      <main className="mx-auto max-w-6xl px-6 md:px-10 pt-8 md:pt-12 pb-16">
        {disclaimer ? (
          <div
            data-testid="credit-improvement-top-disclaimer"
            className="mb-6 rounded-[var(--ds-radius-md)] bg-[var(--ds-color-accent-soft)]/30 border border-[var(--ds-color-accent)]/15 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]"
          >
            {disclaimer}
          </div>
        ) : null}
        {children}
      </main>
    </div>
  );
}

function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <header data-testid="credit-improvement-header">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
        {eyebrow}
      </div>
      <h1
        className="mt-2 text-[30px] md:text-[36px] font-semibold leading-tight text-[var(--ds-color-ink)]"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {title}
      </h1>
      <p className="mt-2 text-[14px] text-[var(--ds-color-ink-muted)] max-w-3xl leading-relaxed">
        {subtitle}
      </p>
    </header>
  );
}

function SummaryHeader({ totalCount, priorityCounts, selectedCount, selectedItemIds, reportId }) {
  return (
    <div
      data-testid="credit-improvement-summary"
      className="rounded-[var(--ds-radius-xl)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-card)] p-6 md:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] uppercase text-[var(--ds-color-ink-muted)]">
            <ClipboardList className="h-4 w-4" aria-hidden="true" />
            Findings overview
          </div>
          <div
            data-testid="credit-improvement-total-count"
            className="mt-1 text-[22px] font-semibold text-[var(--ds-color-ink)]"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {totalCount} negative {totalCount === 1 ? "item" : "items"} detected
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {PRIORITY_ORDER.map((p) => {
              const meta = PRIORITY_META[p];
              const Icon = meta.Icon;
              const count = priorityCounts[p] || 0;
              return (
                <span
                  key={p}
                  data-testid={`credit-improvement-priority-count-${p}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--ds-color-surface-muted)] border border-[var(--ds-color-border)] px-3 py-1 text-[13px]"
                >
                  <Icon className={`h-3.5 w-3.5 ${meta.color}`} aria-hidden="true" />
                  <span className={meta.color + " font-semibold"}>{meta.label}</span>
                  <span className="text-[var(--ds-color-ink-muted)] tabular-nums">· {count}</span>
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div
            data-testid="credit-improvement-selected-count"
            className="text-[13px] text-[var(--ds-color-ink-muted)]"
          >
            <span className="font-semibold text-[var(--ds-color-ink)] tabular-nums">{selectedCount}</span> selected for dispute
          </div>
          <Link
            to={{
              pathname: "/dispute-letters/new",
              search: `?${new URLSearchParams({
                credit_report_id: reportId,
                items: (selectedItemIds || []).join(","),
              }).toString()}`,
            }}
            data-testid="credit-improvement-prepare-disputes-cta"
            aria-disabled={selectedCount === 0}
            className={`inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-all ${
              selectedCount > 0
                ? "bg-[var(--ds-color-accent)] text-[var(--ds-color-accent-ink)] hover:bg-[var(--ds-color-accent-strong)] shadow-[var(--ds-shadow-card)]"
                : "bg-[var(--ds-color-surface-muted)] text-[var(--ds-color-ink-subtle)] cursor-not-allowed"
            }`}
            onClick={(e) => {
              if (selectedCount === 0) e.preventDefault();
            }}
          >
            Prepare Dispute Letters
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function InlineDisclaimer() {
  const REQUIRED_DISCLAIMER =
    "This app does not provide legal, financial, or credit repair guarantees. It provides educational analysis and draft documents based on information you provide. Approval, funding, score increases, and account removals are not guaranteed. Please review all information before submitting documents or applying for funding.";
  return (
    <div
      data-testid="credit-improvement-inline-disclaimer"
      className="rounded-[var(--ds-radius-lg)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface-muted)] px-4 py-3 text-[13px] text-[var(--ds-color-ink-muted)] leading-relaxed"
    >
      {REQUIRED_DISCLAIMER}
    </div>
  );
}

function FiltersRow({
  priorityFilter,
  onPriorityChange,
  bureauFilter,
  onBureauChange,
  categoryFilter,
  onCategoryChange,
  bureausAvailable,
  categoriesAvailable,
}) {
  return (
    <div
      data-testid="credit-improvement-filters"
      className="flex flex-wrap items-center gap-3 rounded-[var(--ds-radius-lg)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] px-4 py-3"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        <FilterIcon className="h-4 w-4" aria-hidden="true" />
        Filter:
      </div>
      <FilterSelect
        testId="credit-improvement-filter-priority"
        label="Priority"
        value={priorityFilter}
        onChange={onPriorityChange}
        options={[
          { value: "all",    label: "All priorities" },
          { value: "high",   label: "High" },
          { value: "medium", label: "Medium" },
          { value: "low",    label: "Low" },
        ]}
      />
      {bureausAvailable.length > 0 ? (
        <FilterSelect
          testId="credit-improvement-filter-bureau"
          label="Bureau"
          value={bureauFilter}
          onChange={onBureauChange}
          options={[
            { value: "all", label: "All bureaus" },
            ...bureausAvailable.map((b) => ({ value: b, label: b })),
          ]}
        />
      ) : null}
      {categoriesAvailable.length > 0 ? (
        <FilterSelect
          testId="credit-improvement-filter-category"
          label="Category"
          value={categoryFilter}
          onChange={onCategoryChange}
          options={[
            { value: "all", label: "All categories" },
            ...categoriesAvailable.map((c) => ({
              value: c,
              label: CATEGORY_LABELS[c] || c,
            })),
          ]}
        />
      ) : null}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] bg-[var(--ds-color-background)] px-2 py-1 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-accent)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FindingsList({ items, reportId, onItemPatched }) {
  // Group by priority and preserve the canonical order.
  const grouped = { high: [], medium: [], low: [] };
  for (const it of items) {
    const p = (it.priority || "low").toLowerCase();
    if (grouped[p]) grouped[p].push(it);
    else grouped.low.push(it);
  }

  return (
    <div data-testid="credit-improvement-findings-list" className="space-y-8">
      {PRIORITY_ORDER.map((p) => {
        const bucket = grouped[p];
        if (bucket.length === 0) return null;
        const meta = PRIORITY_META[p];
        return (
          <div
            key={p}
            data-testid={`credit-improvement-priority-bucket-${p}`}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <span className={`inline-block h-2 w-2 rounded-full ${meta.ring}`} aria-hidden="true" />
              <h3
                data-testid={`credit-improvement-priority-heading-${p}`}
                className={`${meta.color} text-[16px] font-semibold font-semibold uppercase tracking-wide`}
              >
                {meta.label} · {bucket.length}
              </h3>
            </div>
            <div className="space-y-4">
              {bucket.map((it) => (
                <NegativeItemCard
                  key={it.id}
                  reportId={reportId}
                  item={it}
                  onItemPatched={onItemPatched}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
